import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Search, Plus, Minus, Users, Clock, Mail, Shield, Music, Link2, DollarSign, MousePointer, UserPlus, ChevronDown, ChevronUp, MessageSquare, Download } from 'lucide-react';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import AdminMessages from '../components/AdminMessages';

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [minuteAmount, setMinuteAmount] = useState('');
  const [updating, setUpdating] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(null);
  const [detailsError, setDetailsError] = useState({});
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const response = await api.get('/admin/messages');
        if (!cancelled) setUnreadMessages(response.data.total_unread || 0);
      } catch (error) {
        // Silently ignore; badge is non-critical
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchUsers();
      return;
    }

    try {
      setSearching(true);
      const response = await api.get('/admin/search', {
        params: { q: searchQuery }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddMinutes = async (userId, amount) => {
    try {
      setUpdating(true);
      await api.put(`/admin/users/${userId}/minutes`, {
        minutes: parseInt(amount),
        action: 'add'
      });
      toast.success(`Added ${amount} minutes`);
      setEditingUser(null);
      setMinuteAmount('');
      fetchUsers();
    } catch (error) {
      console.error('Failed to add minutes:', error);
      toast.error('Failed to add minutes');
    } finally {
      setUpdating(false);
    }
  };

  const handleSubtractMinutes = async (userId, amount) => {
    try {
      setUpdating(true);
      await api.put(`/admin/users/${userId}/minutes`, {
        minutes: parseInt(amount),
        action: 'subtract'
      });
      toast.success(`Removed ${amount} minutes`);
      setEditingUser(null);
      setMinuteAmount('');
      fetchUsers();
    } catch (error) {
      console.error('Failed to subtract minutes:', error);
      toast.error('Failed to remove minutes');
    } finally {
      setUpdating(false);
    }
  };

  const handleSetMinutes = async (userId, amount) => {
    try {
      setUpdating(true);
      await api.put(`/admin/users/${userId}/minutes`, {
        minutes: parseInt(amount),
        action: 'set'
      });
      toast.success(`Set minutes to ${amount}`);
      setEditingUser(null);
      setMinuteAmount('');
      fetchUsers();
    } catch (error) {
      console.error('Failed to set minutes:', error);
      toast.error('Failed to set minutes');
    } finally {
      setUpdating(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      setExporting(true);
      const response = await api.get('/admin/users/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Users exported');
    } catch (error) {
      console.error('Failed to export users:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to export users');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '-';
    const total = Math.round(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const statusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'complete':
      case 'processed':
        return 'bg-green-500/15 text-green-600';
      case 'error':
        return 'bg-red-500/15 text-red-600';
      case 'uploaded':
        return 'bg-blue-500/15 text-blue-600';
      default:
        return 'bg-amber-500/15 text-amber-600';
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setDetailsLoading(userId);
      setDetailsError((prev) => ({ ...prev, [userId]: null }));
      const response = await api.get(`/admin/users/${userId}/songs`);
      setUserDetails((prev) => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Failed to load user songs:', error);
      const msg = error.response?.status === 403
        ? 'Admin access required'
        : 'Failed to load user details';
      toast.error(msg);
      setDetailsError((prev) => ({ ...prev, [userId]: msg }));
    } finally {
      setDetailsLoading(null);
    }
  };

  const toggleExpand = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(userId);
    if (!userDetails[userId]) {
      fetchUserDetails(userId);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold">Admin Access Required</h2>
          <p className="text-muted-foreground mt-2">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-muted-foreground">Manage users, minutes, and messages</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Users className="h-4 w-4" />
          Users
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${activeTab === 'messages' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <MessageSquare className="h-4 w-4" />
          Messages
          {unreadMessages > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold">
              {unreadMessages}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'messages' ? (
        <AdminMessages />
      ) : (
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Management
              </CardTitle>
              <CardDescription>
                Search users and manage their minute balances
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExportUsers}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setSearchQuery(''); fetchUsers(); }}>
              Clear
            </Button>
          </form>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-center">Minutes</TableHead>
                    <TableHead className="text-center">Tracks</TableHead>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Affiliate</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <React.Fragment key={u.id}>
                      <TableRow className={expandedUser === u.id ? 'border-b-0' : ''}>
                        <TableCell className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleExpand(u.id)}
                          >
                            {expandedUser === u.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate max-w-[180px]" title={u.email}>{u.email || 'No email'}</span>
                            {u.is_admin && (
                              <span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-600 rounded">Admin</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{u.name || '-'}</TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                            <Clock className="h-3 w-3" />
                            {u.minutes}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-600 font-medium">
                            <Music className="h-3 w-3" />
                            {u.tracks_cleaned || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          {u.referred_by ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Link2 className="h-3 w-3 text-blue-500" />
                              <span className="text-blue-600 font-medium">{u.referred_by.affiliate_code}</span>
                              {u.referred_by.converted && (
                                <span className="text-xs text-green-600">(paid)</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.affiliate ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-600 rounded font-medium">
                                {u.affiliate.code}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog.Root open={editingUser === u.id} onOpenChange={(open) => {
                            if (open) {
                              setEditingUser(u.id);
                              setMinuteAmount('');
                            } else {
                              setEditingUser(null);
                            }
                          }}>
                            <Dialog.Trigger asChild>
                              <Button variant="outline" size="sm">
                                Edit
                              </Button>
                            </Dialog.Trigger>
                            <Dialog.Portal>
                              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
                              <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background rounded-lg p-6 w-full max-w-md z-50 shadow-xl">
                                <Dialog.Title className="text-lg font-semibold mb-1">
                                  Edit Minutes
                                </Dialog.Title>
                                <Dialog.Description className="text-sm text-muted-foreground mb-4">
                                  {u.email} - Current balance: {u.minutes} minutes
                                </Dialog.Description>

                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="minutes">Minutes Amount</Label>
                                    <Input
                                      id="minutes"
                                      type="number"
                                      min="0"
                                      placeholder="Enter amount"
                                      value={minuteAmount}
                                      onChange={(e) => setMinuteAmount(e.target.value)}
                                      className="mt-1"
                                    />
                                  </div>

                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => handleAddMinutes(u.id, minuteAmount)}
                                      disabled={!minuteAmount || updating}
                                      className="flex-1"
                                    >
                                      {updating ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <Plus className="h-4 w-4 mr-2" />
                                      )}
                                      Add
                                    </Button>
                                    <Button
                                      onClick={() => handleSubtractMinutes(u.id, minuteAmount)}
                                      disabled={!minuteAmount || updating}
                                      variant="outline"
                                      className="flex-1"
                                    >
                                      {updating ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <Minus className="h-4 w-4 mr-2" />
                                      )}
                                      Remove
                                    </Button>
                                  </div>

                                  <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                      <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                                    </div>
                                  </div>

                                  <Button
                                    onClick={() => handleSetMinutes(u.id, minuteAmount)}
                                    disabled={!minuteAmount || updating}
                                    variant="secondary"
                                    className="w-full"
                                  >
                                    Set to Exact Amount
                                  </Button>
                                </div>

                                <Dialog.Close asChild>
                                  <Button
                                    variant="ghost"
                                    className="absolute right-4 top-4"
                                    size="sm"
                                  >
                                    &times;
                                  </Button>
                                </Dialog.Close>
                              </Dialog.Content>
                            </Dialog.Portal>
                          </Dialog.Root>
                        </TableCell>
                      </TableRow>
                      
                      {expandedUser === u.id && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={9} className="p-4">
                            {detailsLoading === u.id ? (
                              <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                              </div>
                            ) : detailsError[u.id] ? (
                              <div className="flex flex-col items-center justify-center gap-2 py-6 mb-4">
                                <p className="text-sm text-muted-foreground">{detailsError[u.id]}</p>
                                <Button variant="outline" size="sm" onClick={() => fetchUserDetails(u.id)}>
                                  Retry
                                </Button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <Music className="h-4 w-4 text-green-500" />
                                    Uploaded Songs
                                    {userDetails[u.id]?.total_songs != null && (
                                      <span className="ml-1 text-xs text-muted-foreground">
                                        ({userDetails[u.id].total_songs})
                                      </span>
                                    )}
                                  </h4>
                                  {(userDetails[u.id]?.songs?.length || 0) === 0 ? (
                                    <p className="text-sm text-muted-foreground">No songs uploaded yet</p>
                                  ) : (
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                      {userDetails[u.id].songs.map((song) => (
                                        <div key={song.id} className="flex items-center justify-between gap-2 text-sm border-b last:border-b-0 pb-2 last:pb-0">
                                          <div className="min-w-0 flex-1">
                                            <p className="font-medium truncate" title={song.title}>
                                              {song.title}
                                              {song.archived && (
                                                <span className="ml-1 text-xs text-muted-foreground">(archived)</span>
                                              )}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              {formatDate(song.created_at)} · {formatDuration(song.duration)}
                                            </p>
                                          </div>
                                          <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full capitalize ${statusColor(song.status)}`}>
                                            {song.status}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <Users className="h-4 w-4 text-blue-500" />
                                    Account Details
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between gap-2">
                                      <span className="text-muted-foreground">Account ID:</span>
                                      <span className="font-mono text-xs truncate max-w-[160px]" title={u.id}>{u.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Joined:</span>
                                      <span className="font-medium">{formatDate(userDetails[u.id]?.account?.created_at || u.created_at)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Last Activity:</span>
                                      <span className="font-medium">{formatDate(userDetails[u.id]?.account?.updated_at || u.updated_at)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Minutes Balance:</span>
                                      <span className="font-medium">{userDetails[u.id]?.account?.minutes ?? u.minutes}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Total Songs:</span>
                                      <span className="font-medium">{userDetails[u.id]?.total_songs ?? '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Custom Curse Words:</span>
                                      <span className="font-medium">{userDetails[u.id]?.account?.curse_words_count ?? '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Admin:</span>
                                      {(userDetails[u.id]?.account?.is_admin ?? u.is_admin) ? (
                                        <span className="text-amber-600 font-medium">Yes</span>
                                      ) : (
                                        <span className="text-muted-foreground">No</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {u.referred_by && (
                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <Link2 className="h-4 w-4 text-blue-500" />
                                    Referred By
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Affiliate Code:</span>
                                      <span className="font-medium">{u.referred_by.affiliate_code}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Affiliate Email:</span>
                                      <span className="font-medium truncate max-w-[150px]" title={u.referred_by.affiliate_email}>
                                        {u.referred_by.affiliate_email || '-'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Converted (Paid):</span>
                                      <span className={u.referred_by.converted ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                                        {u.referred_by.converted ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {u.affiliate ? (
                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <DollarSign className="h-4 w-4 text-green-500" />
                                    Affiliate Stats
                                  </h4>
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center gap-2">
                                      <MousePointer className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-muted-foreground">Clicks:</span>
                                      <span className="font-medium">{u.affiliate.clicks}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <UserPlus className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-muted-foreground">Sign-ups:</span>
                                      <span className="font-medium">{u.affiliate.signups}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-muted-foreground">Conversions:</span>
                                      <span className="font-medium">{u.affiliate.conversions}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <DollarSign className="h-3 w-3 text-green-500" />
                                      <span className="text-muted-foreground">Earnings:</span>
                                      <span className="font-medium text-green-600">${u.affiliate.earnings?.toFixed(2) || '0.00'}</span>
                                    </div>
                                  </div>
                                  {u.affiliate.paypal_email && (
                                    <div className="mt-3 pt-3 border-t text-sm">
                                      <span className="text-muted-foreground">PayPal:</span>
                                      <span className="ml-2 font-medium">{u.affiliate.paypal_email}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    Affiliate Status
                                  </h4>
                                  <p className="text-sm text-muted-foreground">Not an affiliate</p>
                                </div>
                              )}
                              
                              {!u.referred_by && !u.affiliate && (
                                <div className="bg-background rounded-lg p-4 border">
                                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                    <Link2 className="h-4 w-4 text-muted-foreground" />
                                    Referral Status
                                  </h4>
                                  <p className="text-sm text-muted-foreground">Not referred by anyone</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 'users' && (
        <div className="text-sm text-muted-foreground text-center">
          Total users: {users.length}
        </div>
      )}
    </div>
  );
}
